import { useMemo } from 'react';
import { Link, useParams } from 'react-router-dom';
import { allArticles } from '@/mocks/articles';
import { categories } from '@/mocks/categories';

export default function Article() {
  const { slug } = useParams<{ slug: string }>();
  const article = useMemo(() => allArticles.find((a) => a.slug === slug), [slug]);
  const category = useMemo(
    () => (article ? categories.find((c) => c.slug === article.category) : null),
    [article],
  );
  const relatedArticles = useMemo(
    () =>
      article
        ? allArticles
            .filter((a) => a.category === article.category && a.id !== article.id)
            .slice(0, 3)
        : [],
    [article],
  );

  if (!article) {
    return (
      <main className="pt-24 pb-20">
        <div className="max-w-3xl mx-auto px-4 md:px-6 text-center py-20">
          <div className="w-16 h-16 mx-auto mb-4 rounded-full bg-background-100 flex items-center justify-center">
            <i className="ri-file-unknow-line text-foreground-300 text-2xl"></i>
          </div>
          <h1 className="font-heading text-2xl font-bold text-foreground-900 mb-2">文章未找到</h1>
          <p className="text-foreground-500 text-sm mb-6">该文章可能已被删除或链接无效</p>
          <Link
            to="/"
            className="inline-flex items-center gap-2 px-5 py-2.5 bg-primary-500 text-background-50 rounded-lg text-sm font-medium hover:bg-primary-600 transition-all whitespace-nowrap"
          >
            <i className="ri-arrow-left-line"></i> 返回首页
          </Link>
        </div>
      </main>
    );
  }

  return (
    <main className="pt-24 pb-20">
      <div className="max-w-4xl mx-auto px-4 md:px-6">
        {/* Breadcrumb */}
        <nav className="flex items-center gap-2 text-xs text-foreground-400 mb-8 flex-wrap">
          <Link to="/" className="hover:text-primary-600 transition-colors whitespace-nowrap">
            首页
          </Link>
          <i className="ri-arrow-right-s-line"></i>
          {category && (
            <>
              <Link
                to={`/category/${category.slug}`}
                className="hover:text-primary-600 transition-colors whitespace-nowrap"
              >
                {category.name}
              </Link>
              <i className="ri-arrow-right-s-line"></i>
            </>
          )}
          <span className="text-foreground-700 font-medium truncate max-w-[200px]">
            {article.title}
          </span>
        </nav>

        {/* Article Header */}
        <header className="mb-10">
          <div className="flex items-center gap-2 mb-4 flex-wrap">
            {article.tags.map((tag) => (
              <span
                key={tag}
                className="px-2.5 py-0.5 rounded-md bg-primary-50 text-primary-600 text-xs font-medium"
              >
                {tag}
              </span>
            ))}
          </div>
          <h1 className="font-heading text-3xl md:text-4xl font-bold text-foreground-900 mb-4 leading-tight">
            {article.title}
          </h1>
          <div className="flex items-center gap-4 text-foreground-400 text-xs">
            <span className="flex items-center gap-1">
              <i className="ri-user-line"></i> {article.author}
            </span>
            <span className="flex items-center gap-1">
              <i className="ri-calendar-line"></i> {article.publishDate}
            </span>
            <span className="flex items-center gap-1">
              <i className="ri-time-line"></i> {article.readTime} 分钟阅读
            </span>
          </div>
        </header>

        {/* Featured Image */}
        <div className="aspect-[16/9] rounded-xl overflow-hidden mb-10">
          <img
            src={article.imageUrl}
            alt={article.title}
            className="w-full h-full object-cover"
          />
        </div>

        {/* Article Content */}
        <article
          className="prose-custom text-foreground-700 leading-relaxed text-sm"
          dangerouslySetInnerHTML={{ __html: article.content }}
        />

        {/* Share */}
        <div className="mt-12 pt-8 border-t border-background-200/70">
          <div className="flex items-center gap-3">
            <span className="text-xs text-foreground-400">分享文章：</span>
            <button className="w-9 h-9 flex items-center justify-center rounded-lg bg-background-100 text-foreground-500 hover:bg-primary-50 hover:text-primary-500 transition-all cursor-pointer">
              <i className="ri-wechat-line text-sm"></i>
            </button>
            <button className="w-9 h-9 flex items-center justify-center rounded-lg bg-background-100 text-foreground-500 hover:bg-primary-50 hover:text-primary-500 transition-all cursor-pointer">
              <i className="ri-weibo-line text-sm"></i>
            </button>
            <button className="w-9 h-9 flex items-center justify-center rounded-lg bg-background-100 text-foreground-500 hover:bg-primary-50 hover:text-primary-500 transition-all cursor-pointer">
              <i className="ri-share-forward-line text-sm"></i>
            </button>
          </div>
        </div>

        {/* Related Articles */}
        {relatedArticles.length > 0 && (
          <section className="mt-12 pt-8 border-t border-background-200/70">
            <h3 className="font-heading font-bold text-foreground-900 mb-6 text-lg">
              相关文章推荐
            </h3>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-5">
              {relatedArticles.map((ra) => (
                <Link
                  key={ra.id}
                  to={`/article/${ra.slug}`}
                  className="group rounded-xl bg-background-50 border border-background-200/70 overflow-hidden hover:border-primary-200 transition-all duration-300 cursor-pointer"
                >
                  <div className="aspect-[16/10] overflow-hidden">
                    <img
                      src={ra.imageUrl}
                      alt={ra.title}
                      className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                    />
                  </div>
                  <div className="p-4">
                    <h4 className="font-heading font-semibold text-foreground-900 text-sm group-hover:text-primary-600 transition-colors line-clamp-2">
                      {ra.title}
                    </h4>
                    <p className="text-foreground-400 text-xs mt-2">{ra.publishDate}</p>
                  </div>
                </Link>
              ))}
            </div>
          </section>
        )}
      </div>

      {/* Article Content Styles */}
      <style>{`
        .prose-custom h2 {
          font-family: var(--font-heading);
          font-size: 1.35rem;
          font-weight: 700;
          color: oklch(var(--foreground-900));
          margin-top: 2.5rem;
          margin-bottom: 1rem;
          padding-bottom: 0.5rem;
          border-bottom: 2px solid oklch(var(--primary-100));
        }
        .prose-custom h2:first-child {
          margin-top: 0;
        }
        .prose-custom p {
          margin-bottom: 1rem;
          line-height: 1.85;
          color: oklch(var(--foreground-700));
        }
        .prose-custom strong {
          color: oklch(var(--foreground-900));
          font-weight: 600;
        }
        .prose-custom a {
          color: oklch(var(--primary-600));
          text-decoration: underline;
          text-underline-offset: 2px;
        }
        .prose-custom a:hover {
          color: oklch(var(--primary-500));
        }
      `}</style>
    </main>
  );
}